import React from 'react';
import BenefitsSection from './common/BenefitsSection';
import ProductCTA from './common/ProductCTA';
import PageSEO from './common/PageSEO';
import ClientLogos from './ClientLogos';
import { useT } from '../i18n';

const T = {
  es: {
    seo: {
      title: 'Cadenia Documents - Documento de Control Digital, eCMR y Firma Electrónica',
      description: 'Documentos de transporte digitales con firma electrónica avanzada. Documento de Control obligatorio desde octubre 2026, Carta de Porte digital y eCMR conforme al protocolo europeo.',
      keywords: 'documento control digital, eCMR, carta de porte digital, firma electrónica transporte, cumplimiento normativo 2026, BlueCMR, digitalización transporte',
    },
    benefits: [
      {
        title: 'Cumplimiento Normativo Garantizado',
        description: 'Documento de Control digital obligatorio desde octubre 2026. Genere documentos conformes a la normativa vigente sin cambiar su operativa.',
      },
      {
        title: 'Eliminación del Papel',
        description: 'Sustituya los documentos en papel por versiones digitales con firma electrónica, trazabilidad completa y conservación segura.',
      },
      {
        title: 'Integración Nativa con BlueCMR',
        description: 'Tecnología BlueCMR integrada de forma nativa. Una de las plataformas líderes en documentos de transporte digitales, del grupo Cadenia.',
      },
    ],
    hero: {
      badge: 'CADENIA DOCUMENTS',
      description: 'Genere, comparta y firme documentos de transporte desde Cadenia o mediante API. Documento de Control, Carta de Porte y eCMR con firma electrónica avanzada y trazabilidad completa.',
      cta: 'Ver Demo de Documents',
    },
    mockup: {
      moduleLabel: 'Módulo',
      sidebarItems: ['Soporte', 'Mi Perfil'],
      pageTitle: 'Documentos',
      toolbarButtons: ['Filtros', 'Ordenación'],
      refresh: 'Actualizar',
      tabs: ['Borrador', 'Activo', 'Completado'],
      headers: {
        document: 'Documento',
        route: 'Origen · Destino',
        carrier: 'Transportista',
        signatures: 'Firmas',
        weight: 'Peso',
      },
      rows: [
        { ref: '2040334493', date: '20/4', type: 'Doc. Control', from: 'Planta Valladolid', to: 'Barcelona, 08110', carrier: 'TR. NORTE S.L.', firmas: ['E', 'T', 'D'], signed: [true, true, false], peso: '12.000 KG' },
        { ref: '2040334491', date: '20/4', type: 'Doc. Control', from: 'Planta Valladolid', to: 'Barcelona, 08110', carrier: 'TR. NORTE S.L.', firmas: ['E', 'T', 'D'], signed: [true, true, false], peso: '12.000 KG' },
        { ref: '2040391877', date: '18/4', type: 'Doc. Control', from: 'Planta Valladolid', to: 'Vitoria, 01015', carrier: 'TR. NORTE S.L.', firmas: ['E', 'T', 'D'], signed: [true, false, false], peso: '12.000 KG' },
        { ref: '2040313334', date: '17/4', type: 'Doc. Control', from: 'Planta Valladolid', to: 'Campllong, 17457', carrier: 'TR. Santos S.A.', firmas: ['E', 'T', 'D'], signed: [true, true, true], peso: '8.000 KG' },
        { ref: '2040313335', date: '17/4', type: 'Doc. Control', from: 'Planta Valladolid', to: 'Barcelona, 08028', carrier: 'TR. Santos S.A.', firmas: ['E', 'T', 'D'], signed: [true, true, true], peso: '6.000 KG' },
      ],
      rowsMobile: [
        { ref: '2040334493', date: '20/4', type: 'Doc. Control', from: 'Planta Valladolid', to: 'Barcelona, 08110', carrier: 'TR. NORTE', firmas: ['E', 'T', 'D'], signed: [true, true, false], peso: '12.000 KG' },
        { ref: '2040391877', date: '18/4', type: 'Doc. Control', from: 'Planta Valladolid', to: 'Vitoria, 01015', carrier: 'TR. NORTE', firmas: ['E', 'T', 'D'], signed: [true, false, false], peso: '12.000 KG' },
        { ref: '2040313334', date: '17/4', type: 'Doc. Control', from: 'Planta Valladolid', to: 'Campllong, 17457', carrier: 'TR. Santos', firmas: ['E', 'T', 'D'], signed: [true, true, true], peso: '8.000 KG' },
      ],
    },
    docTypes: {
      title: 'Documentos de Transporte Digitales',
      subtitle: 'Genere, comparta y firme documentos de transporte conformes a la normativa vigente, directamente desde Cadenia.',
      control: {
        title: 'Documento de Control',
        description: 'Obligatorio en formato digital en España desde octubre 2026. Genere documentos válidos para inspección en tránsito, conformes a los requisitos técnicos oficiales.',
        badge: 'Obligatorio desde Oct. 2026',
      },
      waybill: {
        title: 'Carta de Porte Digital',
        description: 'Documentación contractual nacional digital que incluye los datos obligatorios del transporte. Sustituye al Documento de Control cuando contiene toda la información requerida.',
        badge: 'Contractual nacional',
      },
      ecmr: {
        title: 'eCMR',
        description: 'Conforme al Protocolo eCMR y marcos europeos de interoperabilidad. Puede incluir menciones del Documento de Control y Carta de Porte para transporte internacional.',
        badge: 'Protocolo CMR europeo',
      },
    },
    lifecycle: {
      title: 'Ciclo de Vida del Documento',
      subtitle: 'Desde la creación hasta la conservación, cada paso queda registrado con evidencias verificables.',
      stepLabel: 'Paso',
      steps: [
        {
          step: '1',
          title: 'Crear',
          description: 'Genere el documento desde Cadenia o mediante API desde su TMS/ERP. Datos precargados y plantillas reutilizables.',
        },
        {
          step: '2',
          title: 'Compartir',
          description: 'Envíe por email, enlace, QR, SMS o WhatsApp. Comparta en solo lectura o envíe para firma.',
        },
        {
          step: '3',
          title: 'Firmar',
          description: 'Firma electrónica biométrica, sello digital o verificación OTP. Válida desde cualquier dispositivo.',
        },
        {
          step: '4',
          title: 'Conservar',
          description: 'Documento cerrado, inmutable y accesible. Conservación de evidencias hasta 10 años con disponibilidad 99% SLA.',
        },
      ],
    },
    signature: {
      title: 'Firma Electrónica Avanzada',
      description: 'Múltiples métodos de firma adaptados a cada participante del transporte. Conductores firman desde el móvil, cargadores y destinatarios desde cualquier dispositivo.',
      items: [
        'Firma biométrica manuscrita en dispositivo',
        'Sello digital asociado a usuario o empresa',
        'Verificación OTP por SMS o email',
        'Envío y firma masiva de documentos',
      ],
    },
    ecmrDoc: {
      subject: 'Este transporte queda sometido al Convenio CMR',
      idLabel: 'ID:',
      refLabel: 'Ref. Envío:',
      parties: 'Partes Intervinientes',
      shipperLabel: 'Cargador / Expedidor',
      shipperAddress: 'C/ Ejemplo 23, 46001 Valencia, ES',
      consigneeLabel: 'Destinatario',
      carrierLabel: 'Transportista',
      placesTitle: 'Lugares de Carga y Descarga',
      loadingLabel: 'Lugar de Carga',
      unloadingLabel: 'Lugar de Descarga',
      goodsTitle: 'Mercancía',
      goodsDescription: 'Materiales de construcción',
      grossWeightLabel: 'Peso bruto:',
      grossWeight: '12.700 KG',
      actions: ['PDF', 'Enviar', 'Firmar', 'Más'],
    },
    integration: {
      title: 'Integrado con Cadenia Visibility',
      description: 'Genere documentos de transporte directamente desde los envíos de Cadenia Visibility. Los datos del envío se trasladan automáticamente al documento, sin duplicar información.',
      items: [
        'Generación automática desde envíos existentes',
        'Datos de origen, destino y mercancías precargados',
        'Eventos del documento reflejados en el envío',
        'Acceso a documentos firmados desde la ficha del envío',
      ],
    },
    audience: {
      title: 'Para Toda la Cadena',
      shippers: {
        title: 'Cargadores',
        description: 'Genere documentos digitales para sus envíos, comparta con transportistas y mantenga trazabilidad completa de cada expedición.',
      },
      carriers: {
        title: 'Transportistas',
        description: 'Reciba y firme documentos en ruta desde el móvil. Elimine el papel y cumpla con la normativa de documento de control obligatorio.',
      },
      recipients: {
        title: 'Destinatarios',
        description: 'Acceda a los documentos de sus recepciones, firme la entrega digitalmente y conserve evidencia verificable.',
      },
    },
    cta: {
      features: [
        'Documento de Control Digital',
        'Carta de Porte Digital',
        'eCMR',
        'Firma Electrónica',
        'Integración con Visibility',
      ],
    },
  },
  en: {
    seo: {
      title: 'Cadenia Documents - Digital Control Document, eCMR and Electronic Signature',
      description: 'Digital transport documents with advanced electronic signature. Digital control document mandatory from October 2026, digital consignment note and eCMR compliant with the European protocol.',
      keywords: 'digital control document, eCMR, digital consignment note, transport electronic signature, 2026 regulatory compliance, BlueCMR, transport digitization',
    },
    benefits: [
      {
        title: 'Guaranteed Regulatory Compliance',
        description: 'Digital control document mandatory from October 2026. Generate fully compliant documents without changing the way you operate.',
      },
      {
        title: 'Go Paperless',
        description: 'Replace paper documents with digital versions featuring electronic signature, full traceability and secure archiving.',
      },
      {
        title: 'Native BlueCMR Integration',
        description: 'BlueCMR technology natively integrated. One of the leading digital transport document platforms, part of the Cadenia group.',
      },
    ],
    hero: {
      badge: 'CADENIA DOCUMENTS',
      description: 'Generate, share and sign transport documents from Cadenia or via API. Digital control document, consignment note and eCMR with advanced electronic signature and full traceability.',
      cta: 'View Documents Demo',
    },
    mockup: {
      moduleLabel: 'Module',
      sidebarItems: ['Support', 'My Profile'],
      pageTitle: 'Documents',
      toolbarButtons: ['Filters', 'Sorting'],
      refresh: 'Refresh',
      tabs: ['Draft', 'Active', 'Completed'],
      headers: {
        document: 'Document',
        route: 'Origin · Destination',
        carrier: 'Carrier',
        signatures: 'Signatures',
        weight: 'Weight',
      },
      rows: [
        { ref: '2040334493', date: '20/4', type: 'Control Doc.', from: 'Valladolid Plant', to: 'Barcelona, 08110', carrier: 'TR. NORTE S.L.', firmas: ['S', 'C', 'R'], signed: [true, true, false], peso: '12,000 KG' },
        { ref: '2040334491', date: '20/4', type: 'Control Doc.', from: 'Valladolid Plant', to: 'Barcelona, 08110', carrier: 'TR. NORTE S.L.', firmas: ['S', 'C', 'R'], signed: [true, true, false], peso: '12,000 KG' },
        { ref: '2040391877', date: '18/4', type: 'Control Doc.', from: 'Valladolid Plant', to: 'Vitoria, 01015', carrier: 'TR. NORTE S.L.', firmas: ['S', 'C', 'R'], signed: [true, false, false], peso: '12,000 KG' },
        { ref: '2040313334', date: '17/4', type: 'Control Doc.', from: 'Valladolid Plant', to: 'Campllong, 17457', carrier: 'TR. Santos S.A.', firmas: ['S', 'C', 'R'], signed: [true, true, true], peso: '8,000 KG' },
        { ref: '2040313335', date: '17/4', type: 'Control Doc.', from: 'Valladolid Plant', to: 'Barcelona, 08028', carrier: 'TR. Santos S.A.', firmas: ['S', 'C', 'R'], signed: [true, true, true], peso: '6,000 KG' },
      ],
      rowsMobile: [
        { ref: '2040334493', date: '20/4', type: 'Control Doc.', from: 'Valladolid Plant', to: 'Barcelona, 08110', carrier: 'TR. NORTE', firmas: ['S', 'C', 'R'], signed: [true, true, false], peso: '12,000 KG' },
        { ref: '2040391877', date: '18/4', type: 'Control Doc.', from: 'Valladolid Plant', to: 'Vitoria, 01015', carrier: 'TR. NORTE', firmas: ['S', 'C', 'R'], signed: [true, false, false], peso: '12,000 KG' },
        { ref: '2040313334', date: '17/4', type: 'Control Doc.', from: 'Valladolid Plant', to: 'Campllong, 17457', carrier: 'TR. Santos', firmas: ['S', 'C', 'R'], signed: [true, true, true], peso: '8,000 KG' },
      ],
    },
    docTypes: {
      title: 'Digital Transport Documents',
      subtitle: 'Generate, share and sign transport documents compliant with current regulations, directly from Cadenia.',
      control: {
        title: 'Digital Control Document',
        description: 'Mandatory in digital format in Spain from October 2026. Generate documents valid for roadside inspection, compliant with the official technical requirements.',
        badge: 'Mandatory from Oct. 2026',
      },
      waybill: {
        title: 'Digital Consignment Note',
        description: 'Digital national contractual documentation that includes all mandatory transport details. It replaces the digital control document when it contains all the required information.',
        badge: 'National contractual',
      },
      ecmr: {
        title: 'eCMR',
        description: 'Compliant with the eCMR Protocol and European interoperability frameworks. It can include digital control document and consignment note references for international transport.',
        badge: 'European CMR protocol',
      },
    },
    lifecycle: {
      title: 'Document Lifecycle',
      subtitle: 'From creation to archiving, every step is recorded with verifiable evidence.',
      stepLabel: 'Step',
      steps: [
        {
          step: '1',
          title: 'Create',
          description: 'Generate the document from Cadenia or via API from your TMS/ERP. Preloaded data and reusable templates.',
        },
        {
          step: '2',
          title: 'Share',
          description: 'Send by email, link, QR, SMS or WhatsApp. Share as read-only or send for signature.',
        },
        {
          step: '3',
          title: 'Sign',
          description: 'Biometric electronic signature, digital seal or OTP verification. Valid from any device.',
        },
        {
          step: '4',
          title: 'Archive',
          description: 'Closed, immutable and accessible document. Evidence retained for up to 10 years with 99% SLA availability.',
        },
      ],
    },
    signature: {
      title: 'Advanced Electronic Signature',
      description: 'Multiple signature methods tailored to every transport participant. Drivers sign from their phone, shippers and consignees from any device.',
      items: [
        'Handwritten biometric signature on device',
        'Digital seal linked to a user or company',
        'OTP verification via SMS or email',
        'Bulk sending and signing of documents',
      ],
    },
    ecmrDoc: {
      subject: 'This transport is subject to the CMR Convention',
      idLabel: 'ID:',
      refLabel: 'Shipment Ref.:',
      parties: 'Parties Involved',
      shipperLabel: 'Shipper / Sender',
      shipperAddress: 'C/ Ejemplo 23, 46001 Valencia, ES',
      consigneeLabel: 'Consignee',
      carrierLabel: 'Carrier',
      placesTitle: 'Places of Loading and Unloading',
      loadingLabel: 'Place of Loading',
      unloadingLabel: 'Place of Unloading',
      goodsTitle: 'Goods',
      goodsDescription: 'Construction materials',
      grossWeightLabel: 'Gross weight:',
      grossWeight: '12,700 KG',
      actions: ['PDF', 'Send', 'Sign', 'More'],
    },
    integration: {
      title: 'Integrated with Cadenia Visibility',
      description: 'Generate transport documents directly from your Cadenia Visibility shipments. Shipment data flows automatically into the document, with no duplicate data entry.',
      items: [
        'Automatic generation from existing shipments',
        'Origin, destination and goods data preloaded',
        'Document events reflected on the shipment',
        'Access signed documents from the shipment record',
      ],
    },
    audience: {
      title: 'For the Entire Supply Chain',
      shippers: {
        title: 'Shippers',
        description: 'Generate digital documents for your shipments, share them with carriers and keep full traceability of every dispatch.',
      },
      carriers: {
        title: 'Carriers',
        description: 'Receive and sign documents on the road from a mobile device. Eliminate paper and comply with the mandatory digital control document regulation.',
      },
      recipients: {
        title: 'Consignees',
        description: 'Access the documents for your deliveries, sign receipt digitally and keep verifiable evidence.',
      },
    },
    cta: {
      features: [
        'Digital Control Document',
        'Digital Consignment Note',
        'eCMR',
        'Electronic Signature',
        'Visibility Integration',
      ],
    },
  },
};

const lifecycleIcons = [
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />,
  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />,
];

const actionIcons = [
  'M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
  'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
  'M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z',
];

const DocumentsDetail = () => {
  const t = useT(T);
  const benefits = t.benefits;

  return (
    <div className="pt-20">
      <PageSEO
        title={t.seo.title}
        description={t.seo.description}
        keywords={t.seo.keywords}
      />
      {/* Hero */}
      <section className="py-32 bg-gradient-to-br from-slate-50 via-white to-[#e8edf5] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(34,52,97,0.08),transparent_50%)] animate-pulse" style={{animationDuration: '10s'}} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(26,40,71,0.06),transparent_50%)] animate-pulse" style={{animationDuration: '14s', animationDelay: '3s'}} />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#e8edf5] border border-[#d1dae8] rounded-full text-sm font-medium text-[#1a2847] mb-8">
              <div className="w-2 h-2 bg-[#2a4078] rounded-full animate-pulse"></div>
              {t.hero.badge}
            </div>

            <h1 className="text-5xl md:text-7xl font-light text-slate-900 mb-6 leading-tight">
              <span className="bg-gradient-to-r from-[#223461] to-[#1a2847] bg-clip-text text-transparent font-medium">
                Cadenia
              </span>
              <span className="block font-light">Documents</span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-600 max-w-4xl mx-auto leading-relaxed mb-12">
              {t.hero.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#demo" className="inline-flex items-center justify-center px-12 py-4 bg-gradient-to-r from-[#223461] to-[#1a2847] hover:from-[#1a2847] hover:to-[#111d38] text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200">
                {t.hero.cta}
              </a>
            </div>
          </div>

          {/* Ilustración — app shell estilo producto */}
          <div className="relative max-w-5xl mx-auto">
            <div className="absolute -inset-4 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl opacity-10 blur-lg"></div>
            <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
              <div className="flex">
                {/* Sidebar */}
                <div className="w-14 md:w-44 bg-slate-50 border-r border-slate-200 py-4 flex flex-col flex-shrink-0">
                  <div className="px-3 md:px-4 mb-5">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-200 rounded-lg"></div>
                  </div>
                  <div className="px-3 md:px-4 mb-1 hidden md:block">
                    <span className="text-[9px] text-slate-400 uppercase tracking-wider font-medium">{t.mockup.moduleLabel}</span>
                  </div>
                  <div className="px-2 md:px-3 mb-1">
                    <div className="flex items-center gap-2 px-2 py-1.5 bg-[#223461] rounded-lg">
                      <div className="w-4 h-4 bg-white/30 rounded flex-shrink-0"></div>
                      <span className="text-[10px] text-white font-medium hidden md:block">Documents</span>
                      <svg className="w-3 h-3 text-white/60 ml-auto hidden md:block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                  <div className="px-2 md:px-3">
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-[#e8edf5]">
                      <div className="w-3.5 h-3.5 rounded flex-shrink-0 bg-[#a3b5d1]"></div>
                      <span className="text-[10px] text-[#1a2847] font-medium hidden md:block">Documents</span>
                    </div>
                  </div>
                  <div className="mt-auto px-2 md:px-3 space-y-0.5">
                    {t.mockup.sidebarItems.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1.5">
                        <div className="w-3.5 h-3.5 bg-slate-200 rounded flex-shrink-0"></div>
                        <span className="text-[10px] text-slate-400 hidden md:block">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Main content */}
                <div className="flex-1 min-w-0">
                  {/* Page header */}
                  <div className="px-5 md:px-8 pt-5 md:pt-6 pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm md:text-base font-medium text-slate-800">{t.mockup.pageTitle}</h3>
                      <div className="flex items-center gap-1.5">
                        {t.mockup.toolbarButtons.map((btn, i) => (
                          <div key={i} className="px-2 py-1 bg-white border border-slate-200 rounded text-[9px] text-slate-500 hidden md:flex items-center gap-1">
                            <div className="w-2.5 h-2.5 bg-slate-300 rounded-sm"></div>
                            {btn}
                          </div>
                        ))}
                        <div className="px-2.5 py-1 bg-[#223461] rounded text-[9px] text-white font-medium">{t.mockup.refresh}</div>
                      </div>
                    </div>
                    {/* Status tabs */}
                    <div className="flex items-center gap-1">
                      {t.mockup.tabs.map((tab, i) => (
                        <div key={i} className={`px-2.5 py-1 rounded-md text-[9px] font-medium ${i === 1 ? 'bg-[#d1dae8] text-[#1a2847] border border-[#a3b5d1]' : 'text-slate-400'}`}>
                          {tab}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Table — desktop */}
                  <div className="px-5 md:px-8 pb-5 md:pb-6">
                    <div className="border border-slate-200 rounded-lg overflow-hidden hidden md:block">
                      {/* Table header */}
                      <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200">
                        <div className="col-span-3"><span className="text-[9px] font-medium text-slate-500 uppercase tracking-wider">{t.mockup.headers.document}</span></div>
                        <div className="col-span-4"><span className="text-[9px] font-medium text-slate-500 uppercase tracking-wider">{t.mockup.headers.route}</span></div>
                        <div className="col-span-2"><span className="text-[9px] font-medium text-slate-500 uppercase tracking-wider">{t.mockup.headers.carrier}</span></div>
                        <div className="col-span-2"><span className="text-[9px] font-medium text-slate-500 uppercase tracking-wider">{t.mockup.headers.signatures}</span></div>
                        <div className="col-span-1"><span className="text-[9px] font-medium text-slate-500 uppercase tracking-wider">{t.mockup.headers.weight}</span></div>
                      </div>
                      {/* Table rows */}
                      {t.mockup.rows.map((row, i) => (
                        <div key={i} className={`grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-slate-50 items-center ${i === 0 ? 'bg-[#e8edf5]/30' : 'hover:bg-slate-50/50'}`}>
                          <div className="col-span-3">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                              <span className="text-[11px] font-mono text-slate-700">{row.ref}</span>
                            </div>
                            <div className="flex items-center gap-1 mt-0.5 ml-3.5">
                              <span className="text-[9px] text-slate-400">{row.date}</span>
                              <span className="text-[8px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded border border-slate-200">{row.type}</span>
                            </div>
                          </div>
                          <div className="col-span-4">
                            <div className="text-[10px] text-slate-600 truncate">{row.from}</div>
                            <div className="text-[10px] text-slate-400 truncate">{row.to}</div>
                          </div>
                          <div className="col-span-2">
                            <span className="text-[10px] text-slate-500 truncate">{row.carrier}</span>
                          </div>
                          <div className="col-span-2">
                            <div className="flex items-center gap-1">
                              {row.firmas.map((f, j) => (
                                <span key={j} className={`text-[8px] font-medium w-4 h-4 rounded flex items-center justify-center ${row.signed[j] ? 'bg-[#d1dae8] text-[#223461]' : 'bg-slate-100 text-slate-400'}`}>{f}</span>
                              ))}
                            </div>
                          </div>
                          <div className="col-span-1">
                            <span className="text-[9px] text-slate-500">{row.peso}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Cards — mobile */}
                    <div className="space-y-2 md:hidden">
                      {t.mockup.rowsMobile.map((row, i) => (
                        <div key={i} className="border border-slate-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span className="text-[11px] font-mono font-medium text-slate-700">{row.ref}</span>
                              <span className="text-[8px] bg-slate-100 text-slate-500 px-1 py-0.5 rounded border border-slate-200">{row.type}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {row.firmas.map((f, j) => (
                                <span key={j} className={`text-[8px] font-medium w-4 h-4 rounded flex items-center justify-center ${row.signed[j] ? 'bg-[#d1dae8] text-[#223461]' : 'bg-slate-100 text-slate-400'}`}>{f}</span>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-[10px] mb-1">
                            <span className="text-slate-600">{row.from}</span>
                            <svg className="w-3 h-3 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                            <span className="text-slate-600">{row.to}</span>
                          </div>
                          <div className="flex items-center gap-2 text-[9px] text-slate-400">
                            <span>{row.carrier}</span>
                            <span>·</span>
                            <span>{row.peso}</span>
                            <span>·</span>
                            <span>{row.date}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BenefitsSection benefits={benefits} />

      {/* Tipos de documentos */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-6">
              {t.docTypes.title}
            </h2>
            <p className="text-xl text-slate-600 max-w-4xl mx-auto">
              {t.docTypes.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Documento de Control */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-[#2a4078] to-[#223461] rounded-xl flex items-center justify-center text-white mb-6">
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-2xl font-light text-slate-900 mb-4">{t.docTypes.control.title}</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t.docTypes.control.description}
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-xs font-medium text-amber-700">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {t.docTypes.control.badge}
              </div>
            </div>

            {/* Carta de Porte */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-[#2a4078] to-[#223461] rounded-xl flex items-center justify-center text-white mb-6">
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-light text-slate-900 mb-4">{t.docTypes.waybill.title}</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t.docTypes.waybill.description}
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#e8edf5] border border-[#a3b5d1] rounded-full text-xs font-medium text-[#1a2847]">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                {t.docTypes.waybill.badge}
              </div>
            </div>

            {/* eCMR */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-[#223461] to-[#1a2847] rounded-xl flex items-center justify-center text-white mb-6">
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-light text-slate-900 mb-4">{t.docTypes.ecmr.title}</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t.docTypes.ecmr.description}
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {t.docTypes.ecmr.badge}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ciclo de vida del documento */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-6">
              {t.lifecycle.title}
            </h2>
            <p className="text-xl text-slate-600 max-w-4xl mx-auto">
              {t.lifecycle.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.lifecycle.steps.map((item, index) => (
              <div key={index} className="relative text-center">
                {index < 3 && (
                  <div className="hidden lg:block absolute top-8 left-1/2 w-full h-0.5 bg-[#a3b5d1] z-0" style={{transform: 'translateX(50%)'}}></div>
                )}
                <div className="w-16 h-16 bg-gradient-to-br from-[#2a4078] to-[#223461] rounded-xl flex items-center justify-center text-white mb-6 mx-auto relative z-10 shadow-lg">
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">{lifecycleIcons[index]}</svg>
                </div>
                <div className="text-xs font-medium text-[#223461] uppercase tracking-wider mb-2">{t.lifecycle.stepLabel} {item.step}</div>
                <h3 className="text-xl font-medium text-slate-900 mb-4">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Firma electronica */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-8">
                {t.signature.title}
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {t.signature.description}
              </p>
              <ul className="space-y-4">
                {t.signature.items.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-2 h-2 bg-[#2a4078] rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Ilustración — vista de documento eCMR */}
            <div className="relative">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden max-w-md mx-auto">
                {/* Document header */}
                <div className="px-5 py-4 border-b border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-base font-medium text-slate-800 mb-1">ECMR</div>
                      <div className="text-[9px] text-slate-400 italic leading-relaxed mb-2">{t.ecmrDoc.subject}</div>
                      <div className="space-y-0.5">
                        <div className="text-[9px] text-slate-500"><span className="text-slate-400">{t.ecmrDoc.idLabel}</span> LS5HYU8H</div>
                        <div className="text-[9px] text-slate-500"><span className="text-slate-400">{t.ecmrDoc.refLabel}</span> 2040386343</div>
                        <div className="text-[9px] text-[#223461] font-medium">BlueCMR</div>
                      </div>
                    </div>
                    {/* QR placeholder */}
                    <div className="w-14 h-14 bg-slate-100 border border-slate-200 rounded flex items-center justify-center flex-shrink-0">
                      <svg className="w-8 h-8 text-slate-300" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="2" y="2" width="6" height="6" rx="1"/><rect x="10" y="2" width="4" height="4" rx="0.5"/><rect x="16" y="2" width="6" height="6" rx="1"/>
                        <rect x="2" y="10" width="4" height="4" rx="0.5"/><rect x="8" y="8" width="3" height="3" rx="0.5"/><rect x="13" y="10" width="3" height="3" rx="0.5"/><rect x="18" y="10" width="4" height="4" rx="0.5"/>
                        <rect x="2" y="16" width="6" height="6" rx="1"/><rect x="10" y="16" width="3" height="3" rx="0.5"/><rect x="16" y="16" width="3" height="3" rx="0.5"/><rect x="20" y="18" width="2" height="4" rx="0.5"/>
                      </svg>
                    </div>
                  </div>
                </div>
                {/* Partes intervinientes */}
                <div className="border-b border-slate-200">
                  <div className="px-5 py-1.5 bg-slate-50">
                    <span className="text-[8px] font-medium text-slate-500 uppercase tracking-wider">{t.ecmrDoc.parties}</span>
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-slate-200">
                    <div className="px-5 py-3">
                      <div className="text-[8px] text-slate-400 uppercase tracking-wider mb-1">{t.ecmrDoc.shipperLabel}</div>
                      <div className="text-[10px] text-slate-700 font-medium">Industrias Levante S.A.</div>
                      <div className="text-[9px] text-slate-500">{t.ecmrDoc.shipperAddress}</div>
                    </div>
                    <div className="px-5 py-3">
                      <div className="text-[8px] text-slate-400 uppercase tracking-wider mb-1">{t.ecmrDoc.consigneeLabel}</div>
                      <div className="text-[10px] text-slate-700 font-medium">Distribuciones Porto Lda.</div>
                      <div className="text-[9px] text-slate-500">2670-273, Loures, PT</div>
                    </div>
                  </div>
                </div>
                {/* Transportista */}
                <div className="border-b border-slate-200">
                  <div className="px-5 py-2">
                    <div className="text-[8px] text-slate-400 uppercase tracking-wider mb-0.5">{t.ecmrDoc.carrierLabel}</div>
                    <div className="text-[10px] text-slate-700 font-medium">Transportes Ibéricos S.L.</div>
                  </div>
                </div>
                {/* Lugares */}
                <div className="border-b border-slate-200">
                  <div className="px-5 py-1.5 bg-slate-50">
                    <span className="text-[8px] font-medium text-slate-500 uppercase tracking-wider">{t.ecmrDoc.placesTitle}</span>
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-slate-200">
                    <div className="px-5 py-2">
                      <div className="text-[8px] text-slate-400 mb-0.5">{t.ecmrDoc.loadingLabel}</div>
                      <div className="text-[9px] text-slate-600">Industrias Levante S.A.</div>
                      <div className="text-[9px] text-slate-400">46001 Valencia, ES</div>
                    </div>
                    <div className="px-5 py-2">
                      <div className="text-[8px] text-slate-400 mb-0.5">{t.ecmrDoc.unloadingLabel}</div>
                      <div className="text-[9px] text-slate-600">Distribuciones Porto</div>
                      <div className="text-[9px] text-slate-400">2670-273 Loures, PT</div>
                    </div>
                  </div>
                </div>
                {/* Mercancía */}
                <div className="border-b border-slate-200 px-5 py-2">
                  <div className="text-[8px] font-medium text-slate-500 uppercase tracking-wider mb-1">{t.ecmrDoc.goodsTitle}</div>
                  <div className="flex items-center justify-between">
                    <div className="text-[9px] text-slate-600">{t.ecmrDoc.goodsDescription}</div>
                    <div className="text-[9px] text-slate-500"><span className="text-slate-400">{t.ecmrDoc.grossWeightLabel}</span> {t.ecmrDoc.grossWeight}</div>
                  </div>
                </div>
                {/* Action bar */}
                <div className="px-5 py-3 bg-slate-50 flex items-center justify-center gap-6">
                  {t.ecmrDoc.actions.map((label, i) => (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${i === 2 ? 'bg-[#d1dae8] border border-[#a3b5d1]' : 'bg-white border border-slate-200'}`}>
                        <svg className={`w-4 h-4 ${i === 2 ? 'text-[#223461]' : 'text-slate-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={actionIcons[i]} /></svg>
                      </div>
                      <span className={`text-[8px] ${i === 2 ? 'text-[#223461] font-medium' : 'text-slate-500'}`}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integracion con Cadenia Visibility */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="lg:order-2">
              <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-8">
                {t.integration.title}
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {t.integration.description}
              </p>
              <ul className="space-y-4">
                {t.integration.items.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-2 h-2 bg-[#2a4078] rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Ilustracion integracion */}
            <div className="lg:order-1 relative">
              <div className="bg-gradient-to-br from-blue-50 to-[#e8edf5] rounded-2xl border border-slate-200 shadow-lg overflow-hidden p-6">
                <div className="flex items-center justify-center gap-6">
                  {/* Visibility card */}
                  <div className="bg-white rounded-xl p-5 shadow-md border border-slate-100 flex-1">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    </div>
                    <div className="text-sm font-medium text-slate-700 mb-2">Visibility</div>
                    <div className="space-y-1.5">
                      <div className="h-1.5 bg-slate-200 rounded w-full"></div>
                      <div className="h-1.5 bg-slate-200 rounded w-3/4"></div>
                      <div className="h-1.5 bg-blue-100 rounded w-5/6"></div>
                    </div>
                  </div>
                  {/* Arrow */}
                  <div className="flex flex-col items-center gap-1">
                    <svg className="w-8 h-8 text-[#2a4078]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                    <div className="text-[10px] text-slate-400 font-medium">AUTO</div>
                  </div>
                  {/* Documents card */}
                  <div className="bg-white rounded-xl p-5 shadow-md border border-[#d1dae8] flex-1">
                    <div className="w-10 h-10 bg-[#d1dae8] rounded-lg flex items-center justify-center mb-3">
                      <svg className="w-5 h-5 text-[#223461]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </div>
                    <div className="text-sm font-medium text-slate-700 mb-2">Documents</div>
                    <div className="space-y-1.5">
                      <div className="h-1.5 bg-[#a3b5d1] rounded w-full"></div>
                      <div className="h-1.5 bg-[#d1dae8] rounded w-4/5"></div>
                      <div className="h-1.5 bg-[#a3b5d1] rounded w-3/4"></div>
                    </div>
                  </div>
                </div>
                {/* Bottom status */}
                <div className="mt-6 bg-white rounded-lg p-3 border border-slate-100 flex items-center gap-3">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div className="h-1.5 bg-slate-200 rounded flex-1"></div>
                  <div className="px-2 py-0.5 bg-green-100 rounded-full">
                    <div className="h-1.5 bg-green-400 rounded w-10"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Para quien */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-6">
              {t.audience.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2a4078] to-[#223461] rounded-xl flex items-center justify-center text-white mb-6 mx-auto">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4">{t.audience.shippers.title}</h3>
              <p className="text-slate-600">{t.audience.shippers.description}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2a4078] to-[#223461] rounded-xl flex items-center justify-center text-white mb-6 mx-auto">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4">{t.audience.carriers.title}</h3>
              <p className="text-slate-600">{t.audience.carriers.description}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#2a4078] to-[#223461] rounded-xl flex items-center justify-center text-white mb-6 mx-auto">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4">{t.audience.recipients.title}</h3>
              <p className="text-slate-600">{t.audience.recipients.description}</p>
            </div>
          </div>
        </div>
      </section>

      <ClientLogos />

      <section id="demo" className="py-24 bg-gray-50">
        <ProductCTA
          productName="documents"
          features={t.cta.features}
        />
      </section>
    </div>
  );
};

export default DocumentsDetail;
